import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { AlertTriangle, Clock, FolderSearch } from 'lucide-react';

/**
 * HomeProblem — Section 1 / Home V2.
 *
 * Frames the real, daily pain of an ophthalmology practice in 3 messages.
 * Sober: white background, single column on left, 3 micro-points on right.
 * No KPI numbers (the client asked: remove unverifiable %).
 */
const COPY = {
  it: {
    eyebrow: 'Il problema reale',
    title_a: 'Lo studio funziona,',
    title_b: 'ma costa troppe ore di lavoro nascosto.',
    lead:
      'Il medico visita. La segreteria rincorre. Le informazioni vivono in posti diversi. A fine giornata, il tempo speso a gestire dati supera quello speso a curare pazienti. È qui che Prime Vision interviene.',
    pains: [
      {
        icon: FolderSearch,
        title: 'Documentazione frammentata',
        body: 'Cartelle, referti e allegati su software diversi. Ritrovare la storia di un paziente diventa una caccia al tesoro.',
      },
      {
        icon: Clock,
        title: 'Tempo del medico bruciato in burocrazia',
        body: 'Compilazione, ricerca, fatturazione, follow-up: lavoro ripetitivo che ruba minuti preziosi a ogni visita.',
      },
      {
        icon: AlertTriangle,
        title: 'Errori e pazienti dimenticati',
        body: 'Quando i flussi non parlano fra loro, qualcosa scappa: un richiamo non fatto, una fattura non emessa, un consenso mancante.',
      },
    ],
  },
  en: {
    eyebrow: 'The real problem',
    title_a: 'The practice works,',
    title_b: 'but it costs too many hidden hours.',
    lead:
      'The doctor examines. The front desk chases. Information lives in different places. By end of day, time spent managing data exceeds time spent treating patients. That\'s where Prime Vision steps in.',
    pains: [
      {
        icon: FolderSearch,
        title: 'Fragmented documentation',
        body: 'Records, reports and attachments scattered across tools. Finding a patient\'s history becomes a treasure hunt.',
      },
      {
        icon: Clock,
        title: 'Doctor\'s time burnt on paperwork',
        body: 'Filling forms, searching, invoicing, follow-up: repetitive work that steals precious minutes from every visit.',
      },
      {
        icon: AlertTriangle,
        title: 'Errors and forgotten patients',
        body: 'When flows don\'t talk to each other, something slips: a recall not made, an invoice not issued, a consent missing.',
      },
    ],
  },
};

export const HomeProblem = () => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;

  return (
    <section
      data-testid="pv-home-problem"
      className="relative bg-white py-20 md:py-28 border-t border-slate-100"
    >
      <div className="pv-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT — title */}
          <div className="lg:col-span-5">
            <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {c.eyebrow}
            </span>
            <h2 className="mt-5 font-heading font-bold tracking-normal text-[30px] sm:text-[36px] md:text-[42px] leading-[1.10] text-pv-ink">
              {c.title_a}
              <br />
              <span className="text-pv-teal">{c.title_b}</span>
            </h2>
            <p className="mt-6 text-slate-600 text-[15.5px] md:text-[16px] leading-[1.7] max-w-md">
              {c.lead}
            </p>
          </div>

          {/* RIGHT — 3 pains */}
          <div className="lg:col-span-7">
            <ul className="space-y-6">
              {c.pains.map((p, i) => {
                const Icon = p.icon;
                return (
                  <li
                    key={i}
                    className="flex items-start gap-5"
                    data-testid={`pv-home-problem-item-${i + 1}`}
                  >
                    <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-pv-ice border border-pv-teal/20">
                      <Icon className="w-4 h-4 text-pv-teal" />
                    </span>
                    <div>
                      <div className="font-heading font-bold text-[16px] md:text-[17.5px] text-pv-ink leading-tight">
                        {p.title}
                      </div>
                      <p className="mt-2 text-[14.5px] md:text-[15px] text-slate-600 leading-relaxed max-w-xl">
                        {p.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeProblem;
